import React from "react";
import * as LucideIcons from "lucide-react";
import { Sparkles } from "lucide-react";
import { useSite } from "../context/SiteContext";

// Tone mapping for service cards
const tones = {
  dark: { bg: "bg-[#3A5A40] text-[#FDFBF7]", accent: "text-[#EAE5D9]", body: "text-[#EAE5D9]" },
  accent: { bg: "bg-[#9F5C45] text-[#FDFBF7]", accent: "text-[#EAE5D9]", body: "text-[#EAE5D9]" },
  surface: { bg: "bg-[#F4F1EA]", accent: "text-[#3A5A40]", body: "text-[#57534E]" },
  secondary: { bg: "bg-[#EAE5D9]", accent: "text-[#3A5A40]", body: "text-[#57534E]" },
  neutral: { bg: "bg-[#FDFBF7] border border-[#E2DCD0]", accent: "text-[#3A5A40]", body: "text-[#57534E]" },
};

// Span pattern based on index, creates the bento-style asymmetric grid
const spanFor = (i, total) => {
  if (i === 0) return "md:col-span-2 md:row-span-2";
  if (i === 3) return "md:col-span-2";
  if (i === total - 1 && (total === 7 || total === 5)) return "md:col-span-2";
  return "";
};

export default function Services() {
  const { site } = useSite();
  const services = site?.services || [];

  return (
    <section id="services" className="relative py-24 md:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-10 mb-16 md:mb-20 items-end">
          <div className="md:col-span-5">
            <p className="overline text-[#9F5C45] mb-6">01 — What we treat</p>
            <h2 className="font-serif font-light text-[#1C1917] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Care for the <em className="italic text-[#3A5A40]">whole</em> of you.
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="text-[#57534E] text-base md:text-lg leading-relaxed">
              From talk therapy to body-centred spa rituals, our clinicians weave
              together evidence-based modalities with deep presence. Whatever you
              bring through our door, you will be met with steadiness, warmth and
              zero judgment.
            </p>
          </div>
        </div>

        {services.length === 0 ? (
          <p className="text-[#57534E] italic font-serif text-xl">Services list coming soon.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[minmax(220px,auto)] gap-4">
            {services.map((s, i) => {
              const Icon = LucideIcons[s.icon] || Sparkles;
              const tone = tones[s.tone] || tones.surface;
              const isDark = s.tone === "dark" || s.tone === "accent";
              return (
                <article
                  key={s.id || i}
                  data-testid={`service-card-${i}`}
                  className={`group relative p-8 md:p-10 flex flex-col justify-between transition-transform duration-500 hover:-translate-y-1 ${tone.bg} ${spanFor(i, services.length)}`}
                >
                  <Icon className={`w-7 h-7 ${tone.accent}`} strokeWidth={1.5} />
                  <div>
                    <div className={`overline mb-3 ${isDark ? "text-[#EAE5D9]" : "text-[#57534E]"}`}>
                      0{i + 1}
                    </div>
                    <h3 className={`font-serif text-2xl md:text-3xl leading-tight mb-3 ${isDark ? "" : "text-[#1C1917]"}`}>
                      {s.name}
                    </h3>
                    <p className={`text-sm md:text-base leading-relaxed ${tone.body}`}>
                      {s.blurb}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
